import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Package, Calendar, MapPin, Clock, TrendingUp, Users } from "lucide-react";

export default function StaffDashboard() {
  return (
    <div>
      <PageHeader
        title="Staff Dashboard"
        subtitle="Manage pickups, deliveries, and customer orders"
        actions={
          <Button asChild>
            <Link to="/staff/logistics">View Schedule</Link>
          </Button>
        }
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Pickups</p>
                <p className="text-3xl font-bold mt-1">8</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Deliveries</p>
                <p className="text-3xl font-bold mt-1">12</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Orders</p>
                <p className="text-3xl font-bold mt-1">24</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-3xl font-bold mt-1">86</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Today's Pickup Schedule</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/staff/logistics">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                {
                  id: "PKP-1245",
                  customer: "Priya Sharma",
                  time: "10:00 AM - 12:00 PM",
                  area: "Koramangala",
                  status: "pending" as const,
                },
                {
                  id: "PKP-1246",
                  customer: "Anjali Reddy",
                  time: "2:00 PM - 4:00 PM",
                  area: "Indiranagar",
                  status: "pending" as const,
                },
                {
                  id: "PKP-1247",
                  customer: "Meera Patel",
                  time: "4:00 PM - 6:00 PM",
                  area: "Jayanagar",
                  status: "pending" as const,
                },
              ].map((pickup) => (
                <div
                  key={pickup.id}
                  className="flex items-center gap-4 p-4 rounded-lg border"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{pickup.customer}</p>
                    <p className="text-sm text-muted-foreground">
                      {pickup.time} · {pickup.area}
                    </p>
                  </div>
                  <StatusBadge status={pickup.status} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Pending Measurements</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/staff/measurements">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                {
                  id: "MEAS-234",
                  customer: "Neha Singh",
                  orderId: "ORD-1250",
                  priority: "High",
                },
                {
                  id: "MEAS-235",
                  customer: "Kavya Kumar",
                  orderId: "ORD-1251",
                  priority: "Medium",
                },
                {
                  id: "MEAS-236",
                  customer: "Shreya Iyer",
                  orderId: "ORD-1252",
                  priority: "Low",
                },
              ].map((measurement) => (
                <div
                  key={measurement.id}
                  className="flex items-center gap-4 p-4 rounded-lg border"
                >
                  <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{measurement.customer}</p>
                    <p className="text-sm text-muted-foreground">
                      {measurement.orderId}
                    </p>
                  </div>
                  <Button variant="outline" size="sm">Take Measurement</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              {
                id: "ORD-1248",
                customer: "Priya Sharma",
                items: 3,
                status: "in_progress" as const,
                pickup: "Dec 15, 10:00 AM",
              },
              {
                id: "ORD-1249",
                customer: "Anjali Reddy",
                items: 2,
                status: "confirmed" as const,
                pickup: "Dec 15, 2:00 PM",
              },
              {
                id: "ORD-1250",
                customer: "Meera Patel",
                items: 1,
                status: "pending" as const,
                pickup: "Dec 15, 4:00 PM",
              },
            ].map((order) => (
              <div
                key={order.id}
                className="flex items-center gap-4 p-4 rounded-lg border"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{order.id}</p>
                  <p className="text-sm text-muted-foreground">
                    {order.customer} · {order.items} item{order.items > 1 ? "s" : ""}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pickup: {order.pickup}
                  </p>
                </div>
                <StatusBadge status={order.status} />
                <Button variant="outline" size="sm">View</Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
